import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { Link } from 'react-router';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.98);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

export const PageContainer = styled.section`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding-bottom: 54px;

  @media (max-width: 884px) {
    gap: 1.5rem;
    padding-bottom: 32px;
  }
`;

export const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @media (max-width: 884px) {
    gap: 1.5rem;
  }
`;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const SummaryCard = styled.div`
  border-radius: 12px;
  padding: 20px;
  background: ${({ theme }) => theme.palette.white};
  border: 1px solid ${({ theme }) => theme.palette.greyGrey5};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &:nth-of-type(1) {
    background: rgba(220, 240, 234, 0.5);
  }

  &:nth-of-type(2) {
    background: rgba(215, 226, 255, 0.45);
  }

  &:nth-of-type(3) {
    background: rgba(254, 244, 215, 0.45);
  }

  &:nth-of-type(4) {
    background: rgba(228, 207, 254, 0.35);
  }
`;

export const SummaryLabel = styled.p`
  color: ${({ theme }) => theme.palette.greyGrey1};
  font-size: 15px;
  font-weight: 500;
  line-height: 1.4;
`;

export const SummaryValue = styled.p`
  color: ${({ theme }) => theme.palette.blackBlackMain};
  font-size: 34px;
  font-weight: 600;
  line-height: 1.1;
`;

export const Card = styled.div`
  background: ${({ theme }) => theme.palette.white};
  border: 1px solid ${({ theme }) => theme.palette.greyGrey5};
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 884px) {
    padding: 16px;
  }
`;

export const SectionHeading = styled.h2`
  color: ${({ theme }) => theme.palette.blackBlackMain};
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
`;

export const SectionDescription = styled.p`
  color: ${({ theme }) => theme.palette.greyGrey1};
  font-size: 16px;
  line-height: 1.6;
`;

export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;

  @media (max-width: 884px) {
    grid-template-columns: 1fr;
  }
`;

export const ReferralInput = styled.input`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.palette.greyGrey5};
  border-radius: 8px;
  padding: 14px 16px;
  background: ${({ theme }) => theme.palette.greyGrey4};
  color: ${({ theme }) => theme.palette.greyGrey1};
`;

export const PrimaryActionButton = styled.button`
  border: none;
  border-radius: 8px;
  padding: 14px 24px;
  background: ${({ theme }) => theme.palette.mainBlue};
  color: ${({ theme }) => theme.palette.white};
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const SocialActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    justify-content: center;
  }
`;

export const SocialButton = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: transparent;
  cursor: pointer;
  padding: 0;

  &:hover {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
  }
`;

export const MessageBox = styled.div`
  border-radius: 10px;
  background: ${({ theme }) => theme.palette.greyGrey4};
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const CopyRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const CopyButton = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.palette.blackBlackMain};
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

export const MessageTextArea = styled.textarea<{ disabled?: boolean }>`
  width: 100%;
  min-height: 132px;
  border: none;
  resize: vertical;
  background: transparent;
  color: ${({ theme }) => theme.palette.blackBlackMain};
  font-size: 18px;
  line-height: 1.6;
  outline: none;
  opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};

  @media (max-width: 884px) {
    font-size: 16px;
  }
`;

export const LeaderboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

export const HeaderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

export const ToggleLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${({ theme }) => theme.palette.blackBlackMain};
  font-size: 18px;
  font-weight: 500;
`;

export const Podium = styled.div`
  background: ${({ theme }) => theme.palette.mainBlue};
  border-radius: 12px;
  padding: 24px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: end;
  gap: 1rem;
  min-height: 240px;

  @media (max-width: 884px) {
    min-height: 180px;
    padding: 16px;
  }
`;

export const PodiumColumn = styled.div<{ elevated?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: end;
  gap: 0.75rem;
  transform: ${({ elevated }) => (elevated ? 'translateY(-18px)' : 'none')};
`;

export const Avatar = styled.img`
  width: 72px;
  height: 72px;
  border-radius: 999px;
  object-fit: cover;
  border: 3px solid ${({ theme }) => theme.palette.white};

  @media (max-width: 884px) {
    width: 56px;
    height: 56px;
  }
`;

export const PodiumBlock = styled.div<{ elevated?: boolean }>`
  width: min(130px, 100%);
  height: ${({ elevated }) => (elevated ? '110px' : '80px')};
  border-radius: 10px 10px 0 0;
  background: rgba(255, 255, 255, 0.18);
  display: grid;
  place-items: center;
  color: ${({ theme }) => theme.palette.white};
  font-size: 64px;
  font-weight: 700;

  @media (max-width: 884px) {
    height: ${({ elevated }) => (elevated ? '90px' : '64px')};
    font-size: 48px;
  }
`;

export const LowerGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(280px, 0.9fr);
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

export const LeaderboardTable = styled.div`
  background: ${({ theme }) => theme.palette.white};
  border: 1px solid ${({ theme }) => theme.palette.greyGrey5};
  border-radius: 12px;
  padding: 24px;
`;

export const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 88px 1fr 140px;
  gap: 1rem;
  padding-bottom: 1rem;
  color: ${({ theme }) => theme.palette.blackBlackMain};
  font-weight: 600;

  @media (max-width: 640px) {
    display: none;
  }
`;

export const TableRow = styled.div`
  display: grid;
  grid-template-columns: 88px 1fr 140px;
  gap: 1rem;
  padding: 0.75rem 0;
  color: ${({ theme }) => theme.palette.greyGrey1};
  border-top: 1px solid ${({ theme }) => theme.palette.greyGrey5};

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 0.4rem;
    padding: 0.9rem 0;

    & > div:first-of-type {
      font-weight: 600;
      color: ${({ theme }) => theme.palette.blackBlackMain};
    }
  }
`;

export const PrizePanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 16px;
  text-align: center;
`;

export const PrizeCarouselFrame = styled.div`
  width: 100%;
  min-height: 280px;
  display: grid;
  place-items: center;
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  overflow: hidden;

  @media (max-width: 1024px) {
    min-height: 240px;
  }
`;

export const PrizeImage = styled.img`
  max-width: 100%;
  max-height: 260px;
  height: auto;
  object-fit: contain;
  animation: ${fadeInUp} 0.45s ease;
`;

export const PrizeDots = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const PrizeDot = styled.button<{ active?: boolean }>`
  width: ${({ active }) => (active ? '28px' : '10px')};
  height: 10px;
  border-radius: 999px;
  border: none;
  padding: 0;
  background: ${({ theme, active }) =>
    active ? theme.palette.mainBlue : theme.palette.greyGrey3};
  cursor: pointer;
  transition: width 0.2s ease, background-color 0.2s ease;
`;

export const PrizeText = styled.p`
  color: ${({ theme }) => theme.palette.blackBlackMain};
  font-size: 18px;
  font-weight: 500;
`;

export const PrizeLink = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  font: inherit;
  color: ${({ theme }) => theme.palette.mainBlue};
  font-size: 16px;
  text-decoration: underline;
  cursor: pointer;
  &:focus-visible { outline: 2px solid currentColor; outline-offset: 4px; }
`;

export const StatusNotice = styled.div`
  border-radius: 10px;
  background: ${({ theme }) => theme.palette.blueLight};
  padding: 16px;
  color: ${({ theme }) => theme.palette.greyGrey1};
  line-height: 1.6;
`;

export const StatusActionLink = styled(Link)`
  color: ${({ theme }) => theme.palette.mainBlue};
  font-weight: 500;
  text-decoration: underline;
`;

export const EmptyState = styled.div`
  border: 1px dashed ${({ theme }) => theme.palette.greyGrey3};
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  color: ${({ theme }) => theme.palette.greyGrey1};
  background: ${({ theme }) => theme.palette.white};
`;
